/* eslint-disable max-len */
import React, { useEffect, useMemo, useState } from 'react';
import { useSelector } from 'react-redux';
import { VehicleBrandType } from '@1po/1po-bff-fe-spec/generated/common/vehicle/VehicleDetail';
import { RootState } from 'app/AppStore';
import { DataContainer } from 'components/DataContainer';
import { getLastVehicle, getPlateReferences } from 'domains/catalog/Catalog.store';
import { PlateReferencesDetail } from 'domains/catalog/Catalog.types';
import { getFirstGenericPartWithLaborTimes } from 'domains/laborTime/LaborTime.store';
import { getDHReference, getDHReferences, getPricesMap, getStocksMapV2 } from 'domains/references';

import {
  useFetchCrossSellingReferences,
  useFetchPrices,
  useFetchReferencesStocks,
} from 'domains/references/References.requests';
import { getCatalogReferenceView, getDealerType, getTokenUser, SparePartsViewType, StandardView } from 'domains/user';
import PlateReferenceCard from 'pages/CatalogPage/DH/SubcategorySection/SparePartsSection/ReferenceCardsContainer/PlateReferenceCard';
import { PlateReferencesHeader } from 'pages/CatalogPage/DH/SubcategorySection/SparePartsSection/ReferenceCardsContainer/PlateReferencesHeader';
import { HighlightWrapper } from 'pages/CatalogPage/DH/SubcategorySection/SparePartsSection/ReferenceCardsContainer/ReferenceCardsContainer.styled';
import { SelectedIndexType } from 'pages/CatalogPage/DH/SubcategorySection/SparePartsSection/SparePartsSection';
import { theme } from 'styles';
import { MarginBox } from 'UI';
import { FOUND, getArrayWithoutUndefinedMembers, getData, isLoading, LOADING, SEARCH_STATUS } from 'utils';
import { IndexType } from 'utils/svg/common';
import { getFilteredAndSortedReferences, getMainReferenceToDisplay } from './utils';

export interface PlateReferencesContainerProps {
  detail: PlateReferencesDetail;
  vehicleBrand: VehicleBrandType;
  sparePartsView: SparePartsViewType;
  isSelected?: boolean;
  setSelectedIndex: (value: ((prevState: SelectedIndexType) => SelectedIndexType) | SelectedIndexType) => void;
  setHighlightedIndex: (value: ((prevState: IndexType) => IndexType) | IndexType) => void;
  plateId?: string;
  firstRefWithPrice: string | undefined;
  firstRefWithPriceNotInCart: string | undefined;
  refIndex: number;
  index?: number;
}

const PlateReferencesContainer = ({
  detail,
  isSelected = false,
  setSelectedIndex,
  setHighlightedIndex,
  plateId,
  sparePartsView,
  vehicleBrand,
  firstRefWithPrice,
  firstRefWithPriceNotInCart,
  refIndex,
  index,
}: PlateReferencesContainerProps) => {
  const vehicle = useSelector(getLastVehicle);
  const vehicleKey = vehicle?.vehicleDetail?.vehicleKey;
  const dealerType = useSelector(getDealerType);
  const referencePlateView = useSelector(getCatalogReferenceView);
  const tokenUserCountry = useSelector(getTokenUser)?.country;
  const mainReference = useSelector((state: RootState) =>
    getDHReference(state, { vehicleKey, referenceNumber: detail.referenceNumber }),
  );
  const mainReferenceData = getData(mainReference);
  const supersessionReferenceNumbers = detail.supersessionChainReferences
    ? [detail.referenceNumber, ...detail.supersessionChainReferences]
    : [detail.referenceNumber];

  const mainAndInitialReferencesPrices = useSelector((state: RootState) =>
    getPricesMap(state, supersessionReferenceNumbers),
  );
  const mainAndInitialReferencesStocks = useSelector((state: RootState) =>
    getStocksMapV2(state, supersessionReferenceNumbers),
  );

  const mainReferenceNumberToDisplay = getMainReferenceToDisplay(
    detail.referenceNumber,
    detail.supersessionChainReferences,
    mainAndInitialReferencesPrices,
    mainAndInitialReferencesStocks,
  );

  const referenceNumbers = [mainReferenceNumberToDisplay, ...(detail.alternativeReferenceNumbers ?? [])];
  const plateReferences = useSelector((state: RootState) => getPlateReferences(state, { plateId, vehicleKey }));
  const plateReferencesData = getData(plateReferences)?.filter((p) => p.index === index);
  //replace linkedReferences from plate references if reference exists on plate index
  const references = useSelector((state: RootState) => getDHReferences(state, { vehicleKey, referenceNumbers })).map(
    (r) => {
      const plateReference = plateReferencesData?.find((p) => p.referenceNumber === r.referenceNumber);
      return {
        ...r,
        linkedReferences: plateReference !== undefined ? plateReference.linkedReferences : r.linkedReferences,
      };
    },
  );

  const prices = useSelector((state: RootState) => getPricesMap(state, referenceNumbers));
  const stocks = useSelector((state: RootState) => getStocksMapV2(state, referenceNumbers));
  const motrioReferenceNumbers = references.filter((r) => r.brand === 'MOTRIO').map((r) => r.referenceNumber);
  const [isMotrioStockPriceLoaded, setIsMotrioStockPriceLoaded] = useState<boolean>(false);
  const isAnyMotrioStockPriceLoading = motrioReferenceNumbers.some((ref) => {
    const motrioStock = stocks.get(ref)?.searchStatus;
    const motrioPrice = prices.get(ref);
    return isLoading(motrioStock) || isLoading(motrioPrice);
  });
  const genericParts = getArrayWithoutUndefinedMembers(references.map((r) => r.genericPart));

  const firstGenericPartWithLaborTimes = useSelector((state: RootState) =>
    getFirstGenericPartWithLaborTimes(state, {
      vehicleKey,
      genericParts,
    }),
  );

  const firstRefWithLaborTimes = firstGenericPartWithLaborTimes
    ? references.find((r) => r.genericPart === firstGenericPartWithLaborTimes)?.referenceNumber
    : undefined;

  useFetchCrossSellingReferences([...(detail.alternativeReferenceNumbers ?? [])], vehicleKey);
  useFetchPrices(motrioReferenceNumbers, vehicleKey);
  useFetchReferencesStocks(motrioReferenceNumbers);

  useEffect(() => {
    if (!isMotrioStockPriceLoaded && !isAnyMotrioStockPriceLoading) {
      setIsMotrioStockPriceLoaded(true);
    }
  }, [isMotrioStockPriceLoaded, isAnyMotrioStockPriceLoading]);

  const referenceStatus: SEARCH_STATUS = useMemo(() => {
    const loadingRefs = references.some((ref) => isLoading(ref.detailStatus));
    return isMotrioStockPriceLoaded && !loadingRefs ? FOUND : LOADING;
  }, [references, isMotrioStockPriceLoaded]);

  const referencesToDisplay = getFilteredAndSortedReferences({
    references,
    dealerType,
    vehicle: vehicle?.vehicleDetail,
    prices,
    stocks,
    country: tokenUserCountry,
  });

  return (
    <MarginBox mt={15} mb={referencePlateView === StandardView ? 30 : 0} data-cy={'plate-reference-container'}>
      <PlateReferencesHeader
        detail={detail}
        vehicleKey={vehicleKey}
        setHighlightedIndex={setHighlightedIndex}
        setSelectedIndex={setSelectedIndex}
        isSelected={isSelected}
        refIndex={refIndex}
      />
      <HighlightWrapper isSelected={isSelected} background={theme.color.white}>
        <MarginBox mx={15} my={15}>
          <DataContainer data={referenceStatus}>
            {referencesToDisplay.map((ref, i) => (
              <PlateReferenceCard
                key={`plate-ref-${ref.referenceNumber}`}
                reference={ref}
                vehicleBrand={vehicleBrand}
                sparePartsView={sparePartsView}
                displayAdditionalInfo={referencesToDisplay.length > 1}
                plateId={plateId}
                firstRefWithPrice={firstRefWithPrice}
                firstRefWithLaborTimes={firstRefWithLaborTimes}
                firstRefWithPriceNotInCart={firstRefWithPriceNotInCart}
                refIndex={refIndex + i}
                displaySupersessionTooltip={
                  mainReferenceData?.initialReferenceNumber !== undefined &&
                  supersessionReferenceNumbers.includes(ref.referenceNumber)
                }
                index={index}
              />
            ))}
          </DataContainer>
        </MarginBox>
      </HighlightWrapper>
    </MarginBox>
  );
};

export default PlateReferencesContainer;
